import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";

const money = n => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const h = n => Number(n || 0).toFixed(2);
const FIELDS = [["annual_entitled", "Annual (entitled)"], ["annual_accrued", "Annual (accrued)"], ["sick", "Sick"], ["alternate", "Alternate"], ["bereavement_used", "Bereavement used"]];

function Adjust({ row, onDone }) {
  const [f, setF] = useState({ field: "annual_entitled", hours: "", mode: "add", reason: "" });
  const [log, setLog] = useState(null); const [err, setErr] = useState("");
  async function go() {
    setErr("");
    try { await api.post(`/leave-tracker/${row.worker_id}/adjust`, { ...f, hours: Number(f.hours) }); setF({ ...f, hours: "", reason: "" }); onDone(); }
    catch (e) { setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Enter hours and a reason"); }
  }
  return (
    <div className="border-t border-white/10 pt-2 mt-2 space-y-2 text-sm" data-testid={`adjust-${row.worker_id}`}>
      <div className="flex gap-2 flex-wrap items-center">
        <select className="input" value={f.field} onChange={e => setF({ ...f, field: e.target.value })} data-testid="adj-field">{FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select className="input" value={f.mode} onChange={e => setF({ ...f, mode: e.target.value })} data-testid="adj-mode"><option value="add">Add / subtract</option><option value="set">Set to</option></select>
        <input type="number" step="0.01" className="input w-24" placeholder="Hours" value={f.hours} onChange={e => setF({ ...f, hours: e.target.value })} data-testid="adj-hours" />
        <input className="input flex-1 min-w-[10rem]" placeholder="Reason (required)" value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} data-testid="adj-reason" />
        <button className="btn-silver text-sm" onClick={go} data-testid="adj-save">Save change</button>
        <button className="btn-ghost text-sm" onClick={() => api.get(`/leave-tracker/${row.worker_id}/log`).then(r => setLog(r.data))} data-testid="adj-log">History</button>
      </div>
      {err && <div className="text-red-400">{err}</div>}
      {log && <table className="table text-xs"><thead><tr><th>When</th><th>Balance</th><th>Before</th><th>After</th><th>Why</th><th>By</th></tr></thead>
        <tbody>{log.map(x => <tr key={x.id}><td>{fmtDate(x.at)}</td><td>{x.field}</td><td>{h(x.before)}</td><td>{h(x.after)}</td><td>{x.reason}</td><td>{x.by}</td></tr>)}</tbody></table>}
    </div>
  );
}

export default function LeaveOverview() {
  const [o, setO] = useState(null); const [rev, setRev] = useState([]); const [open, setOpen] = useState("");
  const load = () => { api.get("/leave-tracker/overview").then(r => setO(r.data)); api.get("/leave-tracker/needs-review").then(r => setRev(r.data)); };
  useEffect(() => { load(); }, []);
  if (!o) return null;
  const low = new Set(o.low);
  return (
    <>
      <div className="card space-y-2" data-testid="leave-overview">
        <div className="flex justify-between flex-wrap gap-2 items-baseline">
          <h2 className="text-silver">Leave overview (Admin, hours)</h2>
          <div data-testid="leave-liability">Total leave liability: <b>{money(o.total_liability)}</b> <span className="text-xs opacity-60">(annual balance × leave rate)</span></div>
        </div>
        <div className="overflow-x-auto"><table className="table text-sm"><thead><tr><th>Worker</th><th>Annual entitled</th><th>Annual accrued</th><th>Annual total</th><th>Sick</th><th>Alt</th><th>Bereav. used</th><th>Leave rate</th><th>Liability</th><th></th></tr></thead>
          <tbody>{o.rows.map(r => (<>
            <tr key={r.worker_id} className={low.has(r.worker_id) ? "text-amber-300" : ""} data-testid={`lo-row-${r.worker_id}`}>
              <td>{r.name}{r.holiday_pay_type === "payg8" && <span className="text-[10px] ml-1 opacity-70">8% PAYG</span>}{low.has(r.worker_id) && <span className="text-[10px] ml-1">low</span>}</td>
              <td>{h(r.annual_entitled)}</td>
              <td title={r.leave_anniversary ? `Available from ${fmtDate(r.leave_anniversary)}` : ""}>{h(r.annual_accrued)}{r.leave_anniversary && r.annual_accrued > 0 && <div className="text-[10px] opacity-60">from {fmtDate(r.leave_anniversary)}</div>}</td>
              <td>{h(r.annual_total)}</td><td>{h(r.sick)}</td><td>{h(r.alternate)}</td><td>{h(r.bereavement_used)}</td>
              <td>{money(r.leave_rate)}</td><td>{money(r.liability)}</td>
              <td><button className="hub-link text-xs" onClick={() => setOpen(open === r.worker_id ? "" : r.worker_id)} data-testid={`lo-adjust-${r.worker_id}`}>Adjust</button></td>
            </tr>
            {open === r.worker_id && <tr key={`${r.worker_id}-adj`}><td colSpan="10"><Adjust row={r} onDone={load} /></td></tr>}
          </>))}</tbody></table></div>
        <div className="text-[11px] opacity-60">Salaried staff don't accrue by hours — enter their annual leave with Adjust. Every change is kept in History.</div>
      </div>
      <div className="card space-y-1" data-testid="leave-needs-review">
        <h2 className="text-silver">Needs review ({rev.length})</h2>
        {rev.length === 0 && <div className="text-sm opacity-50">Nothing to review.</div>}
        {rev.map((x, i) => (
          <div key={x.id || `c${i}`} className="text-sm flex justify-between gap-2 border-b border-white/5 py-1" data-testid={`review-${i}`}>
            <span><b>{x.worker_name || "—"}</b> · {x.issue}</span>
            {x.id && <button className="hub-link text-xs" onClick={() => api.post(`/leave-tracker/needs-review/${x.id}/resolve`).then(load)} data-testid={`review-resolve-${i}`}>Mark reviewed</button>}
          </div>
        ))}
      </div>
    </>
  );
}
